import { DisplayMode, Engine, Keys, PointerScope } from 'excalibur';
import { getAds } from './monetization/ads';
import { installWebAnalytics } from './monetization/analytics';
import { MONETIZATION, applyMonetizationOverrides } from './monetization/config';
import { readEntitlements } from './monetization/entitlements';
import { configureAnalytics } from './monetization/events';
import { getIap } from './monetization/iap';
import { configureLeaderboard } from './monetization/leaderboard';
import { setupMonetization } from './monetization/setup';
import { GameOverScene } from './scenes/game-over-scene';
import { GameScene } from './scenes/game-scene';
import { ScoresScene } from './scenes/scores-scene';
import { ShopScene } from './scenes/shop-scene';
import { TitleScene } from './scenes/title-scene';
import { TutorialScene } from './scenes/tutorial-scene';
import { refreshSidePanelBest } from './ui/side-panel';
import { refreshSceneTheme, getTheme, onThemeChanged, setTheme } from './themes/theme';
import { sfx } from './utils/audio';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from './utils/constants';
import { renderFaceSheet } from './utils/face-sheet';
import { installHandwritingFont, warmUpHandwritingFont } from './utils/fonts';
import { toggleHaptics } from './utils/haptics';
import { Palette } from './utils/palette';
import { readBestScore } from './utils/storage';

const gameScene = new GameScene();

applyMonetizationOverrides(window.location.search);
configureAnalytics(MONETIZATION.ids.analyticsEndpoint);
configureLeaderboard(MONETIZATION.ids.leaderboardUrl);
installWebAnalytics(MONETIZATION.ids.cfAnalyticsToken);

const game = new Engine({
  width: DESIGN_WIDTH,
  height: DESIGN_HEIGHT,
  displayMode: DisplayMode.FitScreen,
  backgroundColor: Palette.paperEdge,
  antialiasing: true,
  suppressPlayButton: true,
  physics: false,
  pointerScope: PointerScope.Canvas,
  scenes: {
    title: new TitleScene(),
    tutorial: new TutorialScene(),
    shop: new ShopScene(),
    scores: new ScoresScene(),
    game: gameScene,
    gameover: new GameOverScene(),
  },
});

const stage = document.getElementById('stage');
if (stage) {
  stage.appendChild(game.canvas);
}

document.addEventListener('contextmenu', (evt) => {
  evt.preventDefault();
});

game.input.keyboard.on('press', (evt) => {
  if (evt.key === Keys.KeyM) {
    sfx.toggleMute();
  } else if (evt.key === Keys.KeyV) {
    toggleHaptics();
  }
});

function applyThemeChrome(): void {
  const theme = getTheme();
  game.backgroundColor = theme.palette.paperEdge;
  const root = document.documentElement;
  root.style.setProperty('--ink', theme.palette.ink.toHex());
  root.style.setProperty('--ink-soft', theme.palette.inkSoft.toHex());
  root.style.setProperty('--paper', theme.palette.paper.toHex());
  root.style.setProperty('--paper-deep', theme.palette.paperDeep.toHex());
  root.style.setProperty('--paper-edge', theme.palette.paperEdge.toHex());
  root.style.setProperty('--blue', theme.palette.blueDeep.toHex());
  root.style.setProperty('--pink', theme.palette.pinkDeep.toHex());
  root.style.setProperty('--mint', theme.palette.mintDeep.toHex());
  root.style.setProperty('--yellow', theme.palette.yellowDeep.toHex());
}

onThemeChanged(() => {
  applyThemeChrome();
  refreshSceneTheme(game.currentScene);
});

applyThemeChrome();

refreshSidePanelBest(readBestScore());

const tutorialLink = document.getElementById('panel-tutorial-link');
if (tutorialLink) {
  tutorialLink.addEventListener('click', () => {
    if (game.currentSceneName === 'game') {
      return;
    }
    sfx.resume();
    void game.goToScene('tutorial');
  });
}

const supportLink = document.getElementById('support-link');
if (supportLink instanceof HTMLAnchorElement) {
  if (MONETIZATION.ids.supportUrl) {
    supportLink.href = MONETIZATION.ids.supportUrl;
  } else {
    supportLink.style.display = 'none';
  }
}

if (import.meta.env.DEV) {
  const debugWindow = window as unknown as {
    __crazyClock?: GameScene;
    __crazyEngine?: Engine;
    __faceSheet?: () => string;
    __crazySetSkin?: (id: string) => string;
    __monetization?: {
      config: typeof MONETIZATION;
      entitlements: () => ReturnType<typeof readEntitlements>;
      purchase: (id: string) => Promise<string>;
      rewarded: () => Promise<string>;
      interstitial: () => Promise<boolean>;
    };
  };
  debugWindow.__crazyClock = gameScene;
  debugWindow.__crazyEngine = game;
  debugWindow.__faceSheet = renderFaceSheet;
  debugWindow.__crazySetSkin = (id: string) => setTheme(id).id;
  debugWindow.__monetization = {
    config: MONETIZATION,
    entitlements: () => readEntitlements(),
    purchase: (id: string) => getIap().purchase(id),
    rewarded: () => getAds().showRewarded(),
    interstitial: () => getAds().showInterstitial(),
  };
}

async function boot(): Promise<void> {
  installHandwritingFont();
  await Promise.race([
    warmUpHandwritingFont(),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 1800);
    }),
  ]);
  await setupMonetization();
  await game.start('title');
}

void boot();
